import { ArrowForwardIos, ArrowBackIos, Assessment, ExpandLess, ExpandMore, Calculate, TipsAndUpdates, Settings, AdminPanelSettings } from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { RootState } from '../../common/Store';
import { PermissionObjectIds } from '../../models/enums/PermissionObjects';
import { UserActions } from '../../models/enums/UserActions';
import { toggleSideMenu } from '../../redux/SideNavBarSlice';
import { hasPermission } from '../../services/AuthService';
import SideBarItem from './SideBarItem';


const drawerWidth = 240;
const drawerWidthWider = 320;

export const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		sideGap: {
			position: 'relative',
			left: '0',
			width: `calc(${theme.spacing(7)} + 1px)`,
			[theme.breakpoints.up('sm')]: {
				width: `calc(${theme.spacing(9)} + 1px)`,
			},
		},
		drawer: {
			width: drawerWidth,
			flexShrink: 0,
			whiteSpace: 'nowrap',
		},
		drawerOpen: {
			width: drawerWidth,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		},
		drawerOpenWider: {
			width: drawerWidthWider,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		},
		drawerClose: {
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			overflowX: 'hidden',
			width: `calc(${theme.spacing(7)} + 1px)`,
			[theme.breakpoints.up('sm')]: {
				width: `calc(${theme.spacing(9)} + 1px)`,
			},
		},
		drawerHoverOpen: {
			zIndex: 1000,
			position: 'fixed',
			width: drawerWidth,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		},
		drawerHoverOpenWider: {
			zIndex: 1000,
			position: 'fixed',
			width: drawerWidthWider,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		},
		drawerHoverClose: {
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			zIndex: 1000,
			position: 'fixed',
			overflowX: 'hidden',
			width: `calc(${theme.spacing(7)} + 1px)`,
			[theme.breakpoints.up('sm')]: {
				width: `calc(${theme.spacing(9)} + 1px)`,
			},
		},
		toolbar: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-end',
			padding: theme.spacing(0, 1),
			// necessary for content to be below app bar
			...theme.mixins.toolbar,
		},
		root: {
			width: '100%',
			maxWidth: 360,
			backgroundColor: theme.palette.background.paper,
		},
		sideBarItem: {
			paddingLeft: theme.spacing(2),
		},
		sideBarItemIndent: {
			paddingLeft: theme.spacing(4),
		},
		FeatureComplete: {
			color: "Green",
		},
		FeaturePartial: {
			color: "Yellow",
		},
		FeatureBarelyWorking: {
			color: "Red",
		}
	}),
);

export default function SideNavBar() {
	const classes = useStyles();
	const theme = useTheme();
	const [menuState, setMenuState] = React.useState<Map<string, boolean>>(new Map<string, boolean>());
	const [hoverState, setHoverState] = React.useState<boolean>(false);
	const [wideState, setWideState] = React.useState<boolean>(false);

	const isOpen = useAppSelector((state: RootState) => state.menuToggle.isSideMenuOpen);
	const dispatch = useAppDispatch();
    const navigate = useNavigate();

	const handleClick = (key: string) => {
		let keepExpanded = false;
		if (keepExpanded){
			//Keep old one expanded
			handleClickKeepExpanded(key);
		} else {
			//Close old one first
			handleClickCloseOld(key);
		}
	};

	const handleClickKeepExpanded = (key: string) => {
		setMenuState(prev => {
			const newMap = new Map(prev);
			newMap.set(key, !(newMap.get(key) ?? false));
			return newMap;
		});
	}

	const handleClickCloseOld = (key: string) => {
		setMenuState(prev => {
			let lastKey = prev.get(key) ?? false;
			const newMap = new Map();
			newMap.set(key, !lastKey);
			setWideState(!lastKey);
			return newMap;
		});
	}

	return (
        <div>
			<div className={classes.sideGap}></div>
			<Drawer
				variant="permanent"
				onMouseEnter={() => setHoverState(true)}
				onMouseLeave={() => setHoverState(false)}
				className={clsx(classes.drawer, {
					[classes.drawerOpen]: isOpen && !wideState,
					[classes.drawerOpenWider]: isOpen && wideState,
					[classes.drawerHoverClose]: !isOpen && !hoverState,
					[classes.drawerHoverOpen]: !isOpen && hoverState && !wideState,
					[classes.drawerHoverOpenWider]: !isOpen && hoverState && wideState
			})}
				classes={{
					paper: clsx({
						[classes.drawerOpen]: isOpen && !wideState,
						[classes.drawerOpenWider]: isOpen && wideState,
						[classes.drawerHoverClose]: !isOpen && !hoverState,
                        [classes.drawerHoverOpen]: !isOpen && hoverState && !wideState,
                        [classes.drawerHoverOpenWider]: !isOpen && hoverState && wideState
                        }),
				}}
			>
				<div className={classes.toolbar}>
					<IconButton onClick={() => dispatch(toggleSideMenu())} size="large">
						{theme.direction === 'rtl' ? <ArrowForwardIos /> : <ArrowBackIos /> }
					</IconButton>
				</div>
				<Divider />

				<List className={classes.root}>
					{/* <ListItem button key="sandbox" className={classes.sideBarItem} onClick={() => navigate("/app/sandbox")}>
						<ListItemIcon>
							<TipsAndUpdates fontSize="large" />
						</ListItemIcon>
						<ListItemText primary="Sandbox" />
					</ListItem> */}
                    {
                        SideBarItem(PermissionObjectIds.QUOTATION)
                    }
                    {
                        SideBarItem(PermissionObjectIds.PURCHASE_ORDER)
                    }
                    {
                        SideBarItem(PermissionObjectIds.INVOICE)
                    }
                    {
                        SideBarItem(PermissionObjectIds.PACKING_LIST)
                    }
                    <Divider />
					{ 
						hasPermission(PermissionObjectIds.ACCOUNT_MASTER_SETUP, UserActions["READ"]) ? 
						(
							<>
								<ListItem button onClick={() => { handleClick("Account"); } }>
									<ListItemIcon>
										<Calculate fontSize="large" />
									</ListItemIcon>
									<ListItemText primary="Account" />
									{(menuState.get("Account") ?? false) ? <ExpandLess /> : <ExpandMore />}
								</ListItem>
								<Collapse in={(menuState.get("Account") ?? false)} timeout="auto" unmountOnExit>
										<List component="div" disablePadding>
										{
											SideBarItem(PermissionObjectIds.BANK_ACCOUNT)
										}  
										{
											SideBarItem(PermissionObjectIds.ACCOUNT_STATEMENT_RECORD)
										}  
										{
											SideBarItem(PermissionObjectIds.ACCOUNT_STATEMENT)
										} 
										{
											SideBarItem(PermissionObjectIds.EXCHANGE_RATE)
										} 
										</List>
								</Collapse>
							</>							
						) : null
					}
                    <Divider />
					<ListItem button onClick={() => { handleClick("System"); } }>
						<ListItemIcon>
							<Settings fontSize="large" />
						</ListItemIcon>
						<ListItemText primary="System Settings" />
						{(menuState.get("System") ?? false) ? <ExpandLess /> : <ExpandMore />}
					</ListItem>
					<Collapse in={(menuState.get("System") ?? false)} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
							{
								SideBarItem(PermissionObjectIds.CUSTOMER)
							}  
							{
								SideBarItem(PermissionObjectIds.PRODUCT_MANAGEMENT)
							}  
							{
								SideBarItem(PermissionObjectIds.PRODUCT_PACKAGE)
							}  
							{
								SideBarItem(PermissionObjectIds.END_USER_LIST)
							}  
							{
								SideBarItem(PermissionObjectIds.DELIVERY_METHOD)
							}  
							{
								SideBarItem(PermissionObjectIds.PAYMENT_TERM)
							}  
							{
								SideBarItem(PermissionObjectIds.PRINT_ENVELOP)
							}  
							{
								SideBarItem(PermissionObjectIds.MANUFACTURER)
							}  
							{
								SideBarItem(PermissionObjectIds.BOX_SIZE)
							}  
							</List>
					</Collapse>
                    <Divider />
					{ 
						hasPermission(PermissionObjectIds.ADMINISTRATION, UserActions["READ"]) ? 
						(
							<>
								<ListItem button onClick={() => { handleClick("Administration"); } }>
									<ListItemIcon>
										<AdminPanelSettings fontSize="large" />
									</ListItemIcon>
									<ListItemText primary="Administration" />
									{(menuState.get("Administration") ?? false) ? <ExpandLess /> : <ExpandMore />}
								</ListItem>
								<Collapse in={(menuState.get("Administration") ?? false)} timeout="auto" unmountOnExit>
										<List component="div" disablePadding>
										{
											SideBarItem(PermissionObjectIds.STAFF_MANAGEMENT)
										}  
										{
											SideBarItem(PermissionObjectIds.STAFF_PERMISSION_MANAGEMENT)
										}  
										{
											SideBarItem(PermissionObjectIds.PERMISSION_MANAGEMENT)
										}  
										{
											SideBarItem(PermissionObjectIds.BRANCH_SETUP)
										}  
										</List>
								</Collapse>
							</>							
						) : null
					}
                    <Divider />
					<ListItem button onClick={() => {handleClick("Reports")}}>
						<ListItemIcon>
							<Assessment fontSize="large" />
						</ListItemIcon>
						<ListItemText primary="Reports" />
						{(menuState.get("Reports") ?? false) ? <ExpandLess /> : <ExpandMore />}
					</ListItem>

					<Collapse in={menuState.get("Reports") ?? false} timeout="auto" unmountOnExit>
						<List component="div" disablePadding>
                            {
                                SideBarItem(PermissionObjectIds.TRADING_QUOTATION_HISTORY)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.TRADING_SALES_HISTORY)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.PRODUCT_ENQUIRY)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.REPORT_SALES_PO)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.REPORT_SALES_ANALYSIS)
                            }
							{
                                SideBarItem(PermissionObjectIds.REPORT_SALES_SUMMARY)
                            }
                            {
                                SideBarItem(PermissionObjectIds.FILING_RECORD_PO)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.FILING_RECORD_INVOICE)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.PO_ENQUIRY)
                            } 
                            {
                                SideBarItem(PermissionObjectIds.PURCHASE_RANKING)
                            }
						</List>
					</Collapse> 
				</List>

			</Drawer>
		</div>
    );
}

